/**
 * Avatar component with initials and hash-based color
 * Displays user initials with a consistent color based on the name
 */

interface AvatarProps {
  fullName?: string | null;
  size?: number;
  className?: string;
}

/**
 * Generate a consistent color from a string
 * Using a simple hash function to convert name to hue value
 */
function stringToColor(str: string | undefined | null): string {
  if (!str || typeof str !== 'string') {
    // Default gray color for undefined/null names
    return 'hsl(0, 0%, 50%)';
  }

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Convert hash to hue (0-360)
  const hue = Math.abs(hash % 360);

  // Use high saturation and medium lightness for vibrant, readable colors
  // In dark mode, increase lightness for better contrast
  return `hsl(${hue}, 70%, 50%)`;
}

/**
 * Extract initials from full name
 * Takes first letter of first name and first letter of last name
 */
function getInitials(fullName: string | undefined | null): string {
  if (!fullName || typeof fullName !== 'string') {
    return '?';
  }

  const names = fullName.trim().split(/\s+/);

  if (names.length === 0 || !names[0]) {
    return '?';
  }

  if (names.length === 1) {
    // Only one name, take first letter
    return names[0][0].toUpperCase();
  }

  // Take first letter of first name and first letter of last name
  const firstInitial = names[0][0];
  const lastInitial = names[names.length - 1][0];

  return (firstInitial + lastInitial).toUpperCase();
}

export default function Avatar({ fullName, size = 40, className = '' }: AvatarProps) {
  const initials = getInitials(fullName);
  const backgroundColor = stringToColor(fullName);
  const displayName = fullName || 'User';

  return (
    <div
      className={`
        inline-flex items-center justify-center
        rounded-lg border-2 border-black dark:border-white
        font-bold text-white
        select-none
        ${className}
      `}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor,
        fontSize: `${size * 0.4}px`,
      }}
      title={displayName}
      aria-label={`Avatar for ${displayName}`}
    >
      {initials}
    </div>
  );
}
